import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Upload, FileText,
  Image as ImageIcon, File, Trash2, Share2, Eye, Lock,
  FolderOpen, Clock, CheckCircle,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems, patientNavItems } from '../components/brand/navItems';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentSharing() {
  const { user, token } = useAuth();
  const location = useLocation();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/documents/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setDocuments(await response.json());
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }

    // Check file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not allowed. Please upload PDF, images, or Word documents.');
      return;
    }

    setUploading(true);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const response = await fetch(`${API_URL}/api/documents/upload`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            filename: file.name,
            file_type: file.type,
            file_size: file.size,
            file_data: base64Data,
            category: selectedCategory === 'all' ? 'general' : selectedCategory
          })
        });

        if (response.ok) {
          toast.success('Document uploaded securely');
          fetchDocuments();
        } else {
          toast.error('Upload failed');
        }
        setUploading(false);
      };
    } catch (error) {
      toast.error('Upload error');
      setUploading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All Documents', icon: FolderOpen },
    { value: 'medical_records', label: 'Medical Records', icon: FileText },
    { value: 'insurance', label: 'Insurance', icon: File },
    { value: 'lab_results', label: 'Lab Results', icon: FileText },
    { value: 'prescriptions', label: 'Prescriptions', icon: FileText },
    { value: 'other', label: 'Other', icon: File }
  ];

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return ImageIcon;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredDocs = selectedCategory === 'all' 
    ? documents 
    : documents.filter(d => d.category === selectedCategory);

  const isPatient = user?.role === 'patient';
  const navItems = isPatient
    ? patientNavItems({ pathname: location.pathname })
    : clinicianNavItems({ pathname: location.pathname });

  return (
    <DashboardShell
      navItems={navItems}
      title="Secure Documents"
      subtitle="HIPAA-ready document storage. Encrypted at rest and in transit, access logged and monitored."
      testId="document-sharing"
    >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-2 text-sm text-cn-text-muted">
            <Lock className="w-4 h-4" />
            HIPAA-ready document storage
          </div>
          
          {/* Upload Button */}
          <div>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleFileUpload}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            />
            <label htmlFor="file-upload">
              <Button
                as="span"
                disabled={uploading}
                className="bg-gradient-to-r from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white rounded-full px-6 cursor-pointer"
                data-testid="upload-button"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </label>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg sticky top-24">
              <CardHeader>
                <CardTitle className="text-sm">Categories</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const count = cat.value === 'all' 
                    ? documents.length 
                    : documents.filter(d => d.category === cat.value).length;
                  
                  return (
                    <button
                      key={cat.value}
                      onClick={() => setSelectedCategory(cat.value)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                        selectedCategory === cat.value
                          ? 'bg-cn-sage-50 dark:bg-cn-sage-900/30 text-cn-primary-hover dark:text-cn-sage-300'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-cn-ink-700/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{cat.label}</span>
                      </div>
                      <span className="text-xs bg-slate-100 dark:bg-cn-ink-700 px-2 py-0.5 rounded-full">
                        {count}
                      </span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Documents Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredDocs.length === 0 ? (
              <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
                <CardContent className="py-16 text-center">
                  <FolderOpen className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">No documents uploaded yet</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Upload your first document to get started
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredDocs.map((doc) => {
                  const FileIcon = getFileIcon(doc.file_type);
                  
                  return (
                    <Card
                      key={doc.id}
                      className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg hover:shadow-xl transition-shadow"
                      data-testid={`document-${doc.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-cn-sage-900/40 dark:to-cn-sage-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileIcon className="w-6 h-6 text-cn-primary dark:text-cn-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">
                              {doc.filename}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span className="px-2 py-0.5 bg-slate-100 dark:bg-cn-ink-700 rounded-full capitalize">
                                {doc.category}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 mt-2 text-xs text-slate-400 dark:text-slate-500">
                              <Clock className="w-3 h-3" />
                              {new Date(doc.uploaded_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-cn-ink-700">
                          <Button variant="outline" size="sm" className="flex-1 rounded-lg text-xs">
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1 rounded-lg text-xs">
                            <Share2 className="w-3 h-3 mr-1" />
                            Share
                          </Button>
                          <Button variant="outline" size="sm" className="rounded-lg text-xs text-red-500 hover:text-red-600">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Security Notice */}
        <Card className="mt-8 bg-gradient-to-r from-cn-sage-50 to-green-50 dark:from-cn-sage-900/20 dark:to-green-900/20 border-0">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-cn-sage-100 dark:bg-cn-sage-900/40 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-cn-primary dark:text-cn-sage-300" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                  Your Documents Are Secure
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  All documents are encrypted at rest and in transit. Access is logged and monitored. 
                  Only you and authorized healthcare providers can view your documents.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
    </DashboardShell>
  );
}
